import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import GameBoard from './components/GameBoard';
import Menu from './components/Menu';
import GameOver from './components/GameOver';

const COLORS = {
  red: '#FF0000',
  orange: '#FF7F00',
  yellow: '#FFFF00',
  green: '#00FF00',
  blue: '#0000FF',
  violet: '#8B00FF'
};

const COLOR_NAMES = ['red', 'orange', 'yellow', 'green', 'blue', 'violet'];

const GAME_CONFIGS = {
  '3x3': {
    easy: { colors: 3, displayTime: 2000 },
    medium: { colors: 3, displayTime: 1500 },
    hard: { colors: 3, displayTime: 1000 }
  },
  '4x4': {
    easy: { colors: 6, displayTime: 3000 },
    medium: { colors: 6, displayTime: 2000 },
    hard: { colors: 6, displayTime: 1500 }
  }
};

function App() {
  const [gameState, setGameState] = useState('menu'); // menu, playing, gameOver
  const [gridSize, setGridSize] = useState('3x3');
  const [difficulty, setDifficulty] = useState('easy');
  const [sequence, setSequence] = useState([]);
  const [showingAllColors, setShowingAllColors] = useState(false);
  const [userSequence, setUserSequence] = useState([]);
  const [revealedCells, setRevealedCells] = useState([]);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(1);
  const [finalScore, setFinalScore] = useState(0);

  const config = GAME_CONFIGS[gridSize][difficulty];

  const generateRandomPosition = useCallback((size, usedPositions) => {
    const gridNum = parseInt(size);
    let attempts = 0;
    
    const isPositionUsed = (pos) => {
      return usedPositions.some(p => p.row === pos.row && p.col === pos.col);
    };
    
    let position = {
      row: Math.floor(Math.random() * gridNum),
      col: Math.floor(Math.random() * gridNum)
    };
    
    while (attempts < 100 && isPositionUsed(position)) {
      position = {
        row: Math.floor(Math.random() * gridNum),
        col: Math.floor(Math.random() * gridNum)
      };
      attempts++;
    }
    
    return position;
  }, []);

  const generateSequence = useCallback(() => {
    const availableColors = COLOR_NAMES.slice(0, config.colors);
    const newSequence = [];
    const usedPositions = [];

    for (let i = 0; i < config.colors; i++) {
      const position = generateRandomPosition(gridSize, usedPositions);
      usedPositions.push(position);
      newSequence.push({
        color: availableColors[i],
        position: position
      });
    }

    return newSequence;
  }, [config.colors, gridSize, generateRandomPosition]);

  const startGame = useCallback(() => {
    setGameState('playing');
    setScore(0);
    setRound(1);
    setUserSequence([]);
    setRevealedCells([]);
    const newSequence = generateSequence();
    setSequence(newSequence);
    setShowingAllColors(true);
  }, [generateSequence]);

  // Show all colors, then hide them
  useEffect(() => {
    if (gameState === 'playing' && showingAllColors && sequence.length > 0) {
      const timer = setTimeout(() => {
        setShowingAllColors(false);
      }, config.displayTime);

      return () => clearTimeout(timer);
    }
  }, [gameState, showingAllColors, sequence, config.displayTime]);

  const handleCellClick = (row, col) => {
    if (showingAllColors || gameState !== 'playing') return;

    // Check if cell was already revealed
    const alreadyRevealed = revealedCells.some(
      cell => cell.row === row && cell.col === col
    );
    if (alreadyRevealed) return;

    // Find if there's a color at this position
    const clickedItem = sequence.find(
      item => item.position.row === row && item.position.col === col
    );

    // If clicked a blank cell (no color here) - game over
    if (!clickedItem) {
      setFinalScore(score);
      setGameState('gameOver');
      return;
    }

    // Check if it's the correct color in ROYGBV order
    const expectedColorIndex = userSequence.length;
    const expectedColor = COLOR_NAMES[expectedColorIndex];
    
    if (clickedItem.color !== expectedColor) {
      // Wrong color - game over
      setFinalScore(score);
      setGameState('gameOver');
      return;
    }

    // Correct! Reveal the cell
    const newRevealedCells = [...revealedCells, { row, col, color: clickedItem.color }];
    setRevealedCells(newRevealedCells);
    const newUserSequence = [...userSequence, clickedItem.color];
    setUserSequence(newUserSequence);

    // Check if sequence is complete
    if (newUserSequence.length === config.colors) {
      // Correct sequence completed
      const newScore = score + (config.colors * 10 * round);
      setScore(newScore);
      setRound(round + 1);
      
      // Start new round
      setTimeout(() => {
        setUserSequence([]);
        setRevealedCells([]);
        const newSequence = generateSequence();
        setSequence(newSequence);
        setShowingAllColors(true);
      }, 1500);
    }
  };

  const handleBackToMenu = () => {
    setGameState('menu');
    setUserSequence([]);
    setRevealedCells([]);
    setSequence([]);
    setShowingAllColors(false);
  };

  return (
    <div className="App">
      {gameState === 'menu' && (
        <Menu
          gridSize={gridSize}
          setGridSize={setGridSize}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
          onStartGame={startGame}
        />
      )}
      
      {gameState === 'playing' && (
        <div className="game-container">
          <div className="game-header">
            <h1>Color Memory Game</h1>
            <div className="game-info">
              <div className="info-item">
                <span className="label">Round:</span>
                <span className="value">{round}</span>
              </div>
              <div className="info-item">
                <span className="label">Score:</span>
                <span className="value">{score}</span>
              </div>
              <div className="info-item">
                <span className="label">Progress:</span>
                <span className="value">{userSequence.length}/{config.colors}</span>
              </div>
            </div>
            <button className="back-button" onClick={handleBackToMenu}>
              Back to Menu
            </button>
          </div>
          
          {showingAllColors && (
            <div className="instruction">Watch carefully and memorize the positions!</div>
          )}
          
          {!showingAllColors && (
            <div className="instruction">
              Click the colors in ROYGBV order:
              <div className="color-guide">
                {COLOR_NAMES.slice(0, config.colors).map((color, idx) => (
                  <span
                    key={color}
                    className={`color-hint ${userSequence[idx] ? 'completed' : ''}`}
                    style={{ backgroundColor: COLORS[color] }}
                  >
                    {color[0].toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <GameBoard
            size={parseInt(gridSize)}
            sequence={sequence}
            showingAllColors={showingAllColors}
            onCellClick={handleCellClick}
            revealedCells={revealedCells}
            colors={COLORS}
          />
        </div>
      )}
      
      {gameState === 'gameOver' && (
        <GameOver
          score={finalScore}
          round={round}
          gridSize={gridSize}
          difficulty={difficulty}
          onBackToMenu={handleBackToMenu}
          onPlayAgain={startGame}
        />
      )}
    </div>
  );
}

export default App;
